import { z } from 'zod';
/**
 * Kiểu đánh số và gạch đầu dòng theo cấp của một `.chilldoc` — APM-SHR-f (người dùng yêu cầu
 * 2026-09-29: "cấu hình các dạng đánh số cho từng cấp (tối đa 3 cấp)", "chọn các symbol làm các
 * cấp gạch đầu dòng", có mẫu sẵn và sửa lại được).
 *
 * Nằm trong `ChilldocSettings.lists`. Mỗi cấp đánh số là một **kiểu số** của chính cấp ấy và một
 * **mẫu** ghép số: `{1}` `{2}` `{3}` là số của cấp 1, 2, 3 (mỗi số viết theo kiểu của cấp nó).
 * "1.1." = mẫu `{1}.{2}.`; "a)" = mẫu `{2})` với kiểu `lower-alpha`. Cấp sâu hơn 3 dùng cấp 3.
 * Hàm thuần — trình soạn, trình xem, và sau này bản in/xuất tệp cùng dựng một chuỗi.
 */
export const LIST_LEVELS = 3;
export const NUMBER_FORMATS = [
    'decimal',
    'lower-alpha',
    'upper-alpha',
    'lower-roman',
    'upper-roman',
];
/** Mẫu một cấp: chữ thường + `{1}`–`{3}`; ≤ 24 ký tự. */
export const LIST_PATTERN_MAX = 24;
export const BULLET_MAX = 3;
export const ListLevelNumbering = z.object({
    format: z.enum(NUMBER_FORMATS),
    pattern: z.string().max(LIST_PATTERN_MAX),
});
export const ChilldocListStyles = z.object({
    /** Đúng 3 cấp; vắng ⇒ đánh số như mặc định của trình soạn. */
    numbering: z.array(ListLevelNumbering).length(LIST_LEVELS).optional().catch(undefined),
    /** Ký hiệu gạch đầu dòng của cấp 1–3 (1–3 ký tự mỗi cấp); vắng ⇒ • ◦ ▪ mặc định. */
    bullets: z
        .array(z.string().trim().min(1).max(BULLET_MAX))
        .length(LIST_LEVELS)
        .optional()
        .catch(undefined),
});
const level = (format, pattern) => ({ format, pattern });
/** Mẫu đánh số dựng sẵn — mục đầu là cách trình soạn vẽ khi chưa cấu hình. */
export const NUMBERING_PRESETS = [
    {
        id: 'decimal',
        levels: [level('decimal', '{1}.'), level('decimal', '{2}.'), level('decimal', '{3}.')],
    },
    {
        id: 'legal',
        levels: [
            level('decimal', '{1}.'),
            level('decimal', '{1}.{2}.'),
            level('decimal', '{1}.{2}.{3}.'),
        ],
    },
    {
        id: 'mixed',
        levels: [level('decimal', '{1}.'), level('lower-alpha', '{2}.'), level('lower-roman', '{3}.')],
    },
    {
        id: 'paren',
        levels: [level('decimal', '{1})'), level('lower-alpha', '{2})'), level('lower-roman', '{3})')],
    },
    {
        id: 'outline',
        levels: [level('upper-roman', '{1}.'), level('upper-alpha', '{2}.'), level('decimal', '{3}.')],
    },
    {
        id: 'step',
        levels: [
            level('decimal', 'Bước {1}:'),
            level('decimal', '{1}.{2}'),
            level('lower-alpha', '{3})'),
        ],
    },
];
/** Mẫu gạch đầu dòng dựng sẵn — mục đầu là mặc định của trình soạn (disc / circle / square). */
export const BULLET_PRESETS = [
    { id: 'classic', symbols: ['•', '◦', '▪'] },
    { id: 'dash', symbols: ['–', '–', '–'] },
    { id: 'arrow', symbols: ['➢', '►', '•'] },
    { id: 'diamond', symbols: ['◆', '◇', '▪'] },
    { id: 'check', symbols: ['✓', '✓', '–'] },
    { id: 'star', symbols: ['★', '☆', '•'] },
];
/** Ký hiệu gợi ý cho ô chọn — người dùng vẫn gõ được ký hiệu khác. */
export const BULLET_SYMBOLS = [
    '•',
    '◦',
    '▪',
    '▫',
    '■',
    '□',
    '●',
    '○',
    '◆',
    '◇',
    '–',
    '—',
    '➢',
    '►',
    '▸',
    '→',
    '✓',
    '✔',
    '★',
    '☆',
    '✦',
    '♦',
    '❖',
    '※',
];
function alpha(value, upper) {
    let n = Math.max(1, Math.floor(value));
    let out = '';
    while (n > 0) {
        n -= 1;
        out = String.fromCharCode(97 + (n % 26)) + out;
        n = Math.floor(n / 26);
    }
    return upper ? out.toUpperCase() : out;
}
const ROMAN = [
    [1000, 'm'],
    [900, 'cm'],
    [500, 'd'],
    [400, 'cd'],
    [100, 'c'],
    [90, 'xc'],
    [50, 'l'],
    [40, 'xl'],
    [10, 'x'],
    [9, 'ix'],
    [5, 'v'],
    [4, 'iv'],
    [1, 'i'],
];
function roman(value, upper) {
    let n = Math.max(1, Math.min(3999, Math.floor(value)));
    let out = '';
    for (const [size, glyph] of ROMAN) {
        while (n >= size) {
            out += glyph;
            n -= size;
        }
    }
    return upper ? out.toUpperCase() : out;
}
/** Một số theo kiểu: 3 ⇒ `3`, `c`, `C`, `iii`, `III`. */
export function formatNumber(value, format) {
    switch (format) {
        case 'lower-alpha':
            return alpha(value, false);
        case 'upper-alpha':
            return alpha(value, true);
        case 'lower-roman':
            return roman(value, false);
        case 'upper-roman':
            return roman(value, true);
        default:
            return String(Math.floor(value));
    }
}
/**
 * Dấu mục của một mục đánh số. `numbers[i]` = số của mục tổ tiên ở cấp i+1 (phần tử cuối là
 * chính mục); cấp > 3 dùng cấu hình cấp 3. `{k}` không có số (mục nằm nông hơn) ⇒ bỏ trống.
 */
export function orderedMarker(levels, numbers) {
    const depth = Math.max(1, numbers.length);
    const own = levels[Math.min(depth, LIST_LEVELS) - 1];
    return own.pattern.replace(/\{([1-3])\}/g, (_match, key) => {
        const index = Number(key) - 1;
        // Cấp > 3: `{3}` là số của **chính** mục (không phải tổ tiên ở cấp 3).
        const number = depth > LIST_LEVELS && index === LIST_LEVELS - 1 ? numbers[depth - 1] : numbers[index];
        if (number === undefined)
            return '';
        return formatNumber(number, levels[index].format);
    });
}
/** Gạch đầu dòng ở độ sâu `depth` (1 = ngoài cùng); cấp > 3 dùng cấp 3. */
export function bulletMarker(bullets, depth) {
    return bullets[Math.min(Math.max(1, depth), LIST_LEVELS) - 1] ?? '•';
}
//# sourceMappingURL=chilldoc-lists.js.map